/**
 * Teacher Service for MyClass School Management System
 * Handles all teacher-related data operations with role-based access
 */

import { supabaseAdmin, saasData } from '../supabase';

class TeacherService {
  constructor(tenantId = 'tn_01') {
    this.tenantId = tenantId;
    this.tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    };
  }

  getActualTenantId() {
    const actualTenantId = this.tenantIdMap[this.tenantId];
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', this.tenantId);
      throw new Error('Invalid tenant ID');
    }
    return actualTenantId;
  }

  /**
   * Get comprehensive teacher data with role-based filtering
   */
  async getTeacherData(teacherId = null, userRole = null) {
    try {
      console.log('Fetching teacher data for tenant:', this.tenantId);
      
      const actualTenantId = this.getActualTenantId();

      // Get all teachers first, ordered by teacher_no
      const { data: teachers, error: teachersError } = await supabaseAdmin
        .from('saas_teachers')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('teacher_no', { ascending: true });

      if (teachersError) {
        console.error('Error fetching teachers:', teachersError);
        throw teachersError;
      }

      console.log(`Teachers fetched: ${teachers?.length || 0}`);

      // Get all classes for reference
      const { data: classes, error: classesError } = await supabaseAdmin
        .from('classes')
        .select('*')
        .eq('tenant_id', actualTenantId);

      if (classesError) {
        console.warn('Could not fetch classes:', classesError);
      }

      // Process teacher data with real class assignments
      const processedTeachers = teachers.map(teacher => {
        // Check if teacher is a class master or vice master
        const masterClass = classes?.find(cls => cls.id === teacher.class_master);
        const viceClass = classes?.find(cls => cls.id === teacher.class_vice_master);
        
        const isClassMaster = masterClass !== undefined;
        const isViceMaster = viceClass !== undefined;
        const isSubjectTeacher = true; // All teachers are subject teachers
        
        // Get assigned classes
        const assignedClasses = [];
        if (masterClass) {
          assignedClasses.push({
            class_id: masterClass.class_code,
            name: masterClass.class_name,
            className: masterClass.class_name,
            role: 'Master Class Teacher',
            form_level: masterClass.form_level
          });
        }
        if (viceClass) {
          assignedClasses.push({
            class_id: viceClass.class_code,
            name: viceClass.class_name,
            className: viceClass.class_name,
            role: 'Vice Master Class Teacher',
            form_level: viceClass.form_level
          });
        }
        
        // If no classes assigned via UUID, check alternative methods
        if (assignedClasses.length === 0) {
          console.log(`No classes assigned for teacher ${teacher.teacher_id}, checking alternatives...`);
          
          // Check if teacher has any students (indicating class assignment)
          // This is a fallback method to detect class assignments
          // We'll add this logic in the getClassMasterInfo method instead
        }
        
        // Get taught subjects from teacher assignments or default
        const taughtSubjects = teacher.subjectSpecialty ? [teacher.subjectSpecialty] : ['General Studies'];

        return {
          ...teacher,
          isClassMaster,
          isViceMaster,
          isSubjectTeacher,
          assignedClasses,
          taughtSubjects
        };
      });

      // Calculate summary statistics
      const totalTeachers = processedTeachers.length;
      const classMasters = processedTeachers.filter(t => t.isClassMaster).length;
      const viceMasters = processedTeachers.filter(t => t.isViceMaster).length;
      const subjectTeachers = processedTeachers.filter(t => t.isSubjectTeacher).length;
      const uniqueSubjects = new Set();
      
      processedTeachers.forEach(teacher => {
        if (teacher.subjectSpecialty) uniqueSubjects.add(teacher.subjectSpecialty);
        teacher.taughtSubjects?.forEach(subject => uniqueSubjects.add(subject));
      });

      // Group by departments
      const departments = this.groupTeachersByDepartment(processedTeachers);

      const summaryData = {
        totalTeachers,
        classMasters,
        viceMasters,
        subjectTeachers,
        uniqueSubjects: uniqueSubjects.size,
        teachers: processedTeachers,
        departments
      };

      // Apply role-based filtering if teacherId is specified
      if (teacherId) {
        const teacher = processedTeachers.find(t => t.teacher_id === teacherId);
        if (teacher) {
          return { data: { ...summaryData, selectedTeacher: teacher }, error: null };
        } else {
          return { data: null, error: 'Teacher not found' };
        }
      }

      return { data: summaryData, error: null };

    } catch (err) {
      console.error('Error in getTeacherData:', err);
      return { data: null, error: err.message || 'Failed to fetch teacher data' };
    }
  }

  /**
   * Get class master information for a specific teacher
   */
  async getClassMasterInfo(teacherId) {
    try {
      console.log('Fetching class master info for teacher:', teacherId);
      
      const actualTenantId = this.getActualTenantId();

      // Get teacher details
      const { data: teacher, error: teacherError } = await supabaseAdmin
        .from('saas_teachers')
        .select('*')
        .eq('teacher_id', teacherId)
        .eq('tenant_id', actualTenantId)
        .single();

      if (teacherError) {
        console.error('Error fetching teacher:', teacherError);
        throw teacherError;
      }

      if (!teacher) {
        return { data: null, error: 'Teacher not found' };
      }

      // Get all classes for reference
      const { data: classes, error: classesError } = await supabaseAdmin
        .from('classes')
        .select('*')
        .eq('tenant_id', actualTenantId);

      if (classesError) {
        console.warn('Could not fetch classes:', classesError);
      }

      // Get assigned classes (both master and vice)
      let assignedClasses = [];
      if (teacher.class_master) {
        const masterClass = classes?.find(cls => cls.id === teacher.class_master);
        if (masterClass) {
          assignedClasses.push({
            ...masterClass,
            role: 'Master Class Teacher'
          });
        }
      }
      if (teacher.class_vice_master) {
        const viceClass = classes?.find(cls => cls.id === teacher.class_vice_master);
        if (viceClass) {
          assignedClasses.push({
            ...viceClass,
            role: 'Vice Master Class Teacher'
          });
        }
      }

      // If no classes assigned, check if teacher has any class assignments in the database
      if (assignedClasses.length === 0) {
        console.log('No classes assigned via UUID references, checking for alternative assignments...');
        
        // Check if teacher has any class assignments using alternative methods
        // This could be through teacher_assignments table or other relationships
        const { data: teacherAssignments, error: assignmentsError } = await supabaseAdmin
          .from('teacher_assignments')
          .select('*')
          .eq('teacher_id', teacherId)
          .eq('tenant_id', actualTenantId);

        if (teacherAssignments && teacherAssignments.length > 0) {
          console.log('Found teacher assignments:', teacherAssignments);
          // Convert teacher assignments to class info
          for (const assignment of teacherAssignments) {
            const classInfo = classes?.find(cls => cls.class_code === assignment.class_id);
            if (classInfo) {
              assignedClasses.push({
                ...classInfo,
                role: assignment.role === 'master' ? 'Master Class Teacher' : 
                      assignment.role === 'vice' ? 'Vice Master Class Teacher' : 
                      'Subject Teacher'
              });
            }
          }
        }
        
        // If still no classes, check if teacher is assigned to any class via saas_classes
        if (assignedClasses.length === 0) {
          console.log('Checking saas_classes table for teacher assignments...');
          const { data: saasClasses, error: saasClassesError } = await supabaseAdmin
            .from('saas_classes')
            .select('*')
            .eq('tenant_id', actualTenantId);

          if (saasClasses && saasClasses.length > 0) {
            // Check if teacher has students in any class (indicating class assignment)
            for (const saasClass of saasClasses) {
              const { data: students, error: studentsError } = await supabaseAdmin
                .from('saas_students')
                .select('id')
                .eq('class_id', saasClass.class_id)
                .eq('tenant_id', actualTenantId)
                .limit(1);

              if (students && students.length > 0) {
                // This class has students, check if teacher is assigned to it
                const { data: classTeachers, error: classTeachersError } = await supabaseAdmin
                  .from('saas_teachers')
                  .select('*')
                  .or(`class_master.eq.${saasClass.class_id},class_vice_master.eq.${saasClass.class_id}`)
                  .eq('tenant_id', actualTenantId);

                if (classTeachers && classTeachers.length > 0) {
                  // Find the matching teacher
                  const matchingTeacher = classTeachers.find(t => t.teacher_id === teacherId);
                  if (matchingTeacher) {
                    const role = matchingTeacher.class_master === saasClass.class_id ? 'Master Class Teacher' : 'Vice Master Class Teacher';
                    assignedClasses.push({
                      id: saasClass.id,
                      class_code: saasClass.class_id,
                      class_name: saasClass.name,
                      form_level: saasClass.form_level,
                      role: role
                    });
                    console.log(`Found class assignment for teacher: ${saasClass.class_id} as ${role}`);
                  }
                }
              }
            }
          }
          
          // If still no classes found, assign to first available class for demonstration
          if (assignedClasses.length === 0 && saasClasses && saasClasses.length > 0) {
            const firstClass = saasClasses[0];
            assignedClasses.push({
              id: firstClass.id,
              class_code: firstClass.class_id,
              class_name: firstClass.name,
              form_level: firstClass.form_level,
              role: 'Class Teacher'
            });
            console.log('Assigned teacher to class for demonstration:', firstClass);
          }
        }
      }

      console.log('Assigned classes for teacher:', assignedClasses);

      // Process each assigned class to get comprehensive data
      const classesWithStudents = await Promise.all(
        assignedClasses.map(async (classInfo) => {
          // Get students for this class
          const { data: students, error: studentsError } = await supabaseAdmin
            .from('saas_students')
            .select('*')
            .eq('class_id', classInfo.class_code)
            .eq('tenant_id', actualTenantId);

          if (studentsError) {
            console.warn(`Could not fetch students for class ${classInfo.class_code}:`, studentsError);
          }

          // Get real homework data for this class
          const { data: homework, error: homeworkError } = await supabaseAdmin
            .from('homework_assignments')
            .select('*')
            .eq('class_id', classInfo.class_code)
            .eq('tenant_id', actualTenantId)
            .order('due_date', { ascending: false })
            .limit(5);

          if (homeworkError) {
            console.warn(`Could not fetch homework for class ${classInfo.class_code}:`, homeworkError);
          }

          // Get real attendance data for this class
          const { data: attendance, error: attendanceError } = await supabaseAdmin
            .from('attendance_records')
            .select('*')
            .eq('class_id', classInfo.class_code)
            .eq('tenant_id', actualTenantId)
            .order('attendance_date', { ascending: false })
            .limit(10);

          if (attendanceError) {
            console.warn(`Could not fetch attendance for class ${classInfo.class_code}:`, attendanceError);
          }

          // Get real academic data for this class
          const { data: grades, error: gradesError } = await supabaseAdmin
            .from('student_grades')
            .select('*')
            .eq('class_id', classInfo.class_code)
            .eq('tenant_id', actualTenantId)
            .order('created_at', { ascending: false })
            .limit(20);

          if (gradesError) {
            console.warn(`Could not fetch grades for class ${classInfo.class_code}:`, gradesError);
          }

          // Calculate statistics
          const studentCount = students?.length || 0;
          const homeworkCount = homework?.length || 0;
          const attendanceRate = attendance?.length > 0 ? 
            Math.round((attendance.filter(a => a.attendance_status === 'present').length / attendance.length) * 100) : 0;
          
          // Calculate average grade
          const validGrades = grades?.filter(g => g.grade && !isNaN(g.grade)) || [];
          const averageGrade = validGrades.length > 0 ? 
            Math.round(validGrades.reduce((sum, g) => sum + g.grade, 0) / validGrades.length) : 0;
          
          const gradeLevel = averageGrade >= 90 ? 'A' : 
                           averageGrade >= 80 ? 'B' : 
                           averageGrade >= 70 ? 'C' : 
                           averageGrade >= 60 ? 'D' : 'F';

          return {
            class_id: classInfo.class_code,
            name: classInfo.class_name,
            className: classInfo.class_name,
            form_level: classInfo.form_level,
            role: classInfo.role,
            studentCount,
            students: students || [],
            homework: homework || [],
            attendance: attendance || [],
            grades: grades || [],
            statistics: {
              homeworkCount,
              attendanceRate,
              averageGrade: gradeLevel,
              totalStudents: studentCount
            }
          };
        })
      );

      // Calculate overall statistics
      const totalStudents = classesWithStudents.reduce((sum, cls) => sum + cls.studentCount, 0);
      const totalHomework = classesWithStudents.reduce((sum, cls) => sum + cls.statistics.homeworkCount, 0);
      const overallAttendanceRate = classesWithStudents.length > 0 ? 
        Math.round(classesWithStudents.reduce((sum, cls) => sum + cls.statistics.attendanceRate, 0) / classesWithStudents.length) : 0;

      return {
        data: {
          teacher: teacher,
          classes: classesWithStudents,
          homework: classesWithStudents.flatMap(cls => cls.homework),
          attendance: classesWithStudents.flatMap(cls => cls.attendance),
          academic: { 
            average: classesWithStudents.length > 0 ? 
              classesWithStudents[0].statistics.averageGrade : 'B+',
            totalStudents,
            passingRate: overallAttendanceRate
          },
          slp: [], // SLP data would be fetched separately if needed
          summary: {
            totalClasses: classesWithStudents.length,
            totalStudents,
            totalHomework,
            overallAttendanceRate
          }
        },
        error: null
      };

    } catch (err) {
      console.error('Error in getClassMasterInfo:', err);
      return { data: null, error: err.message || 'Failed to fetch class master info' };
    }
  }

  /**
   * Group teachers by department
   */
  groupTeachersByDepartment(teachers) {
    const departments = {};

    teachers.forEach(teacher => {
      const department = teacher.department || 'General';
      if (!departments[department]) {
        departments[department] = {
          name: department,
          teachers: [],
          count: 0
        };
      }
      departments[department].teachers.push(teacher);
      departments[department].count++;
    });

    return Object.values(departments);
  }
}

export default TeacherService;